/**
 * QuickReplyButtons Component
 *
 * Prominent clickable buttons for follow-up suggestions on an assistant
 * message. Thin adapter over the shared SuggestionChipRow primitive (Batch 2):
 * maps the raw follow-up strings into chips and delegates rendering, wrapping
 * and the "+N more" collapse.
 *
 * DRY / Rule 15: chip presentation lives in SuggestionChipRow. Import from
 * @axon/ui only.
 *
 * @module QuickReplyButtons
 */

'use client';

import { memo } from 'react';

import { SuggestionChipRow } from './SuggestionChipRow';

// ============================================================================
// Props
// ============================================================================

interface QuickReplyButtonsProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * QuickReplyButtons component
 *
 * @example
 * <QuickReplyButtons
 *   suggestions={['Show details', 'Export data']}
 *   onSuggestionClick={handleClick}
 * />
 */
export const QuickReplyButtons = memo(function QuickReplyButtons({
  suggestions,
  onSuggestionClick,
  disabled = false,
}: QuickReplyButtonsProps) {
  return (
    <SuggestionChipRow
      chips={suggestions.map((s, i) => ({ id: `${i}-${s}`, label: s, value: s }))}
      onSelect={onSuggestionClick}
      disabled={disabled}
    />
  );
});

export default QuickReplyButtons;
